import { useState, useCallback } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { useScreenAlert } from "@hooks/use-screen-alert";

const BASE = "wp-json/smassets/v1/capabilities";

/**
 * WordPress can return role caps as either an array of granted strings
 * ["read", "edit_assets"] or as a boolean map {"read": true, "edit_assets": true}.
 * Normalise to the map form so the rest of the UI can use a single code path.
 */
function normalizeCaps(
  caps: Record<string, boolean> | string[] | undefined
): Record<string, boolean> {
  if (!caps) return {};
  if (Array.isArray(caps)) {
    return Object.fromEntries(caps.map((c) => [c, true]));
  }
  return caps;
}

function normalizeConfig(raw: any): CapConfig {
  const roles: CapConfig["roles"] = {};
  for (const [slug, role] of Object.entries(raw.roles ?? {})) {
    const r = role as any;
    // API returns caps under `current_caps`
    roles[slug] = {
      name: r.name,
      caps: normalizeCaps(r.current_caps ?? r.caps),
      is_default: !!r.is_default,
      is_protected: !!r.is_protected,
    };
  }
  return { ...raw, roles };
}

export type CapConfig = {
  managed_caps: Record<string, string>; // slug → human-readable label
  sections: Record<string, string[]>;
  notes: Record<string, string>;
  roles: Record<string, {
    name: string;
    caps: Record<string, boolean>;
    is_default: boolean;
    is_protected: boolean;
  }>;
};

export type CapOverride = {
  id: number;
  user_id: number;
  user_display: string;
  user_first_name?: string;
  user_last_name?: string;
  user_role?: string;
  user_grade?: number | null;
  cap: string;
  granted: boolean;
  blog_id: number;
};

export const useCapabilitiesAdmin = () => {
  const [config, setConfig] = useState<CapConfig | null>(null);
  const [overrides, setOverrides] = useState<CapOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { getApiUrl } = useMultiSiteContext();
  const { user } = useUserContext();
  const { refreshUserToken } = useRefreshTokenMutation();
  const { setError, setSuccess } = useScreenAlert();

  const apiFetch = useCallback(
    (path: string, options: RequestInit) =>
      fetchWithTokenRefresh(
        getApiUrl(`${BASE}${path}`),
        options,
        () => refreshUserToken(user?.refreshToken ?? "")
      ),
    [getApiUrl, refreshUserToken, user?.refreshToken]
  );

  const headers = useCallback(
    (): Record<string, string> => ({
      Authorization: `Bearer ${user?.authToken ?? ""}`,
      "Content-Type": "application/json",
    }),
    [user?.authToken]
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/config", { method: "GET", headers: headers() });
      if (!res.ok) {
        setError("Failed to load capabilities configuration.");
        return;
      }
      const raw = await res.json();
      setConfig(normalizeConfig(raw));
    } finally {
      setLoading(false);
    }
  }, [apiFetch, headers, setError]);

  const loadOverrides = useCallback(async () => {
    try {
      const res = await apiFetch("/overrides", { method: "GET", headers: headers() });
      if (!res.ok) return;
      const raw = await res.json();
      const rawItems: any[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw.items)     ? raw.items
        : Array.isArray(raw.data)      ? raw.data
        : Array.isArray(raw.overrides) ? raw.overrides
        : [];
      const items: CapOverride[] = rawItems.map((o: any) => {
        // Use || null so empty strings are treated the same as missing fields
        const firstName = o.user_first_name || o.first_name || null;
        const lastName  = o.user_last_name  || o.last_name  || null;
        const fullName  = [firstName, lastName].filter(Boolean).join(" ");
        return {
          id:              Number(o.id),
          user_id:         Number(o.user_id),
          user_display:    o.user_display || o.display_name || fullName || `User #${Number(o.user_id)}`,
          user_first_name: firstName,
          user_last_name:  lastName,
          user_role:       o.user_role || o.role || null,
          user_grade:      o.user_grade ?? o.grade ?? null,
          cap:             o.cap ?? o.capability ?? o.cap_name ?? "",
          // API may return boolean true, integer 1, or string "1"
          granted:         o.granted === true || o.granted === 1 || o.granted === "1",
          blog_id:         Number(o.blog_id ?? 0),
        };
      });
      setOverrides(items);
    } catch {
      // non-critical
    }
  }, [apiFetch, headers]);

  const toggleCap = useCallback(
    async (roleSlug: string, cap: string, grant: boolean) => {
      // Optimistic update
      setConfig((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          roles: {
            ...prev.roles,
            [roleSlug]: {
              ...prev.roles[roleSlug],
              caps: { ...(prev.roles[roleSlug]?.caps ?? {}), [cap]: grant },
            },
          },
        };
      });

      setSaving(true);
      try {
        const res = await apiFetch(`/roles/${encodeURIComponent(roleSlug)}/caps`, {
          method: "PATCH",
          headers: headers(),
          body: JSON.stringify({ cap, grant }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          setError(body?.message ?? "Failed to update capability.");
          // Roll back
          setConfig((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              roles: {
                ...prev.roles,
                [roleSlug]: {
                  ...prev.roles[roleSlug],
                  caps: { ...(prev.roles[roleSlug]?.caps ?? {}), [cap]: !grant },
                },
              },
            };
          });
          return false;
        }
        return true;
      } finally {
        setSaving(false);
      }
    },
    [apiFetch, headers, setError]
  );

  const createRole = useCallback(
    async (slug: string, name: string) => {
      setSaving(true);
      try {
        const res = await apiFetch("/roles", {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({ role_slug: slug, role_name: name }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          setError(body?.message ?? "Failed to create role.");
          return false;
        }
        setSuccess("Role created successfully.");
        await loadConfig();
        return true;
      } finally {
        setSaving(false);
      }
    },
    [apiFetch, headers, loadConfig, setError, setSuccess]
  );

  const deleteRole = useCallback(
    async (slug: string) => {
      setSaving(true);
      try {
        const res = await apiFetch(`/roles/${encodeURIComponent(slug)}`, {
          method: "DELETE",
          headers: headers(),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          setError(body?.message ?? "Failed to delete role.");
          return false;
        }
        setSuccess("Role deleted.");
        await loadConfig();
        return true;
      } finally {
        setSaving(false);
      }
    },
    [apiFetch, headers, loadConfig, setError, setSuccess]
  );

  const resetRole = useCallback(
    async (slug: string) => {
      setSaving(true);
      try {
        const res = await apiFetch(`/roles/${encodeURIComponent(slug)}/reset`, {
          method: "POST",
          headers: headers(),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          setError(body?.message ?? "Failed to reset role.");
          return false;
        }
        setSuccess("Role reset to defaults.");
        await loadConfig();
        return true;
      } finally {
        setSaving(false);
      }
    },
    [apiFetch, headers, loadConfig, setError, setSuccess]
  );

  const addOverride = useCallback(
    async (user_id: number, cap: string, granted: boolean, blog_id?: number) => {
      setSaving(true);
      try {
        const res = await apiFetch("/overrides", {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({ user_id, cap, granted, blog_id }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          setError(body?.message ?? "Failed to add override.");
          return false;
        }
        setSuccess("Override saved.");
        await loadOverrides();
        return true;
      } finally {
        setSaving(false);
      }
    },
    [apiFetch, headers, loadOverrides, setError, setSuccess]
  );

  const deleteOverride = useCallback(
    async (id: number) => {
      setSaving(true);
      try {
        const res = await apiFetch(`/overrides/${id}`, {
          method: "DELETE",
          headers: headers(),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          setError(body?.message ?? "Failed to delete override.");
          return false;
        }
        setOverrides((prev) => prev.filter((o) => o.id !== id));
        return true;
      } finally {
        setSaving(false);
      }
    },
    [apiFetch, headers, setError]
  );

  return {
    config,
    overrides,
    loading,
    saving,
    loadConfig,
    loadOverrides,
    toggleCap,
    createRole,
    deleteRole,
    resetRole,
    addOverride,
    deleteOverride,
  };
};
